import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const CHANNELS = ['Newspaper', 'Banner/Hoarding', 'Word of Mouth', 'Poster/Notice', 'Other'];
const BATCH_PREFERENCES = ['Morning', 'Evening', 'Sundays', 'Weekdays', 'Fast Track'];
const PHONE_RE = /^(\+91)?[6-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = {
  student_name: '',
  student_phone: '',
  preferred_course_id: '',
  attended_by: '',
  channel: '',
  channel_detail: '',
  email: '',
  phone_residence: '',
  address: '',
  date_of_birth: '',
  qualification: '',
  occupation_status: '',
  organization_name: '',
  institution_name: '',
  secondary_interest: '',
  batch_preference: '',
  contact_consent: false,
  office_notes: '',
};

function normalizePhone(raw) {
  let digits = raw.replace(/[\s-]/g, '');
  if (digits.startsWith('+91')) digits = digits.slice(3);
  else if (digits.startsWith('91') && digits.length === 12) digits = digits.slice(2);
  return digits;
}

function validate(form) {
  if (!form.student_name.trim()) return 'Full name is required';
  if (!PHONE_RE.test(form.student_phone.replace(/[\s-]/g, ''))) return 'Enter a valid 10-digit Indian mobile number';
  if (!form.preferred_course_id) return 'Please select a course';
  if (!form.attended_by) return 'Please select who attended the enquiry';
  if (!form.channel) return 'Please select a lead source channel';
  if (!EMAIL_RE.test(form.email.trim())) return 'A valid email is required';
  return null;
}

function groupByCategory(courses) {
  return courses.reduce((acc, c) => {
    const cat = c.category || 'Other';
    (acc[cat] = acc[cat] || []).push(c);
    return acc;
  }, {});
}

export default function WalkInEnquiryDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  // A counselor logging their own walk-in is always the one who attended it —
  // lock the field to their own name instead of letting them pick from the
  // full counselor list. Every other role (branch_manager, front_desk, etc.)
  // keeps the free-choice dropdown, since they're logging on someone else's behalf.
  const isCounselor = user?.role === 'counselor';
  const [form, setForm] = useState(emptyForm);
  const [courses, setCourses] = useState([]);
  const [coursesStatus, setCoursesStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [counselors, setCounselors] = useState([]);
  const [counselorsStatus, setCounselorsStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [submitting, setSubmitting] = useState(false);

  const fetchCourses = useCallback(async () => {
    setCoursesStatus('loading');
    try {
      const { data } = await api.get('/api/courses');
      setCourses(data.courses || []);
      setCoursesStatus('ready');
    } catch (e) {
      console.error(e);
      setCoursesStatus('error');
      toast.error('Failed to load courses — check your connection and retry.');
    }
  }, []);

  const fetchCounselors = useCallback(async () => {
    setCounselorsStatus('loading');
    try {
      const { data } = await api.get('/api/users/counselors');
      setCounselors(data.counselors || []);
      setCounselorsStatus('ready');
    } catch (e) {
      console.error(e);
      setCounselorsStatus('error');
      toast.error("Couldn't load counselor list. Retry.");
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCourses();
      if (isCounselor) {
        setForm(f => ({ ...f, attended_by: user?.name || '' }));
      } else {
        fetchCounselors();
      }
    }
  }, [open, fetchCourses, fetchCounselors, isCounselor, user]);

  const showChannelDetail = form.channel === 'Other' || form.channel === 'Word of Mouth';
  const courseGroups = groupByCategory(courses);

  const resetAndClose = () => {
    setForm(emptyForm);
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate(form);
    if (error) { toast.error(error); return; }

    const payload = {
      intake_mode: 'walk_in',
      student_name: form.student_name.trim(),
      student_phone: normalizePhone(form.student_phone),
      email: form.email.trim(),
      preferred_course_id: form.preferred_course_id,
      attended_by: form.attended_by,
      channel: form.channel,
      contact_consent: !!form.contact_consent,
    };
    if (showChannelDetail && form.channel_detail.trim()) payload.channel_detail = form.channel_detail.trim();
    if (form.phone_residence.trim()) payload.phone_residence = form.phone_residence.trim();
    if (form.address.trim()) payload.address = form.address.trim();
    if (form.date_of_birth) payload.date_of_birth = form.date_of_birth;
    if (form.qualification.trim()) payload.qualification = form.qualification.trim();
    if (form.occupation_status) {
      payload.occupation_status = form.occupation_status;
      if (form.occupation_status === 'working' && form.organization_name.trim()) payload.organization_name = form.organization_name.trim();
      if (form.occupation_status === 'studying' && form.institution_name.trim()) payload.institution_name = form.institution_name.trim();
    }
    if (form.secondary_interest) payload.secondary_interest = form.secondary_interest;
    if (form.batch_preference) payload.batch_preference = form.batch_preference;
    if (form.office_notes.trim()) payload.office_notes = form.office_notes.trim();

    setSubmitting(true);
    try {
      const { data } = await api.post('/api/leads', payload);
      if (data.warning) {
        toast.warning(`A lead with this mobile already exists.${data.existing_lead?.student_name ? ` (${data.existing_lead.student_name})` : ''}`);
      } else {
        toast.success('Walk-in enquiry logged');
      }
      resetAndClose();
      onCreated?.(data.warning ? null : data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) {
        toast.error("You don't have permission to create leads. Contact your admin.");
      } else if (status === 422 && detail) {
        toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      } else {
        toast.error(detail || 'Failed to log walk-in enquiry');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setForm(emptyForm); onOpenChange(v); }}>
      <DialogContent className="rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-foreground text-lg">New Walk-in Enquiry</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Required section */}
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Full Name *</Label>
              <Input data-testid="walkin-name" value={form.student_name} onChange={e => setForm({ ...form, student_name: e.target.value })} className="h-9 rounded-lg text-sm" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Mobile Number *</Label>
                <Input data-testid="walkin-phone" value={form.student_phone} onChange={e => setForm({ ...form, student_phone: e.target.value })} placeholder="9876543210" className="h-9 rounded-lg text-sm" required />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Course *</Label>
                <select data-testid="walkin-course" value={form.preferred_course_id} onChange={e => setForm({ ...form, preferred_course_id: e.target.value })}
                  className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required disabled={coursesStatus !== 'ready'}>
                  {coursesStatus === 'loading' && <option value="" disabled>Loading courses…</option>}
                  {coursesStatus === 'error' && <option value="" disabled>Unable to load courses</option>}
                  {coursesStatus === 'ready' && (
                    <>
                      <option value="">Select course...</option>
                      {Object.entries(courseGroups).map(([cat, list]) => (
                        <optgroup key={cat} label={cat}>
                          {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </optgroup>
                      ))}
                    </>
                  )}
                </select>
                {coursesStatus === 'error' && (
                  <p className="text-[10px] text-destructive mt-1">
                    Unable to load courses.{' '}
                    <button type="button" data-testid="walkin-course-retry" onClick={fetchCourses} className="underline font-semibold">Retry</button>
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Attended By *</Label>
                {isCounselor ? (
                  <Input
                    data-testid="walkin-attended-by-locked"
                    value={form.attended_by}
                    readOnly
                    disabled
                    className="h-9 rounded-lg text-sm bg-secondary/50 text-foreground disabled:opacity-100 disabled:cursor-default"
                  />
                ) : (
                  <>
                    <select data-testid="walkin-attended-by" value={form.attended_by} onChange={e => setForm({ ...form, attended_by: e.target.value })}
                      className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required disabled={counselorsStatus !== 'ready'}>
                      {counselorsStatus === 'loading' && <option value="" disabled>Loading counselors…</option>}
                      {counselorsStatus === 'error' && <option value="" disabled>Couldn't load counselor list</option>}
                      {counselorsStatus === 'ready' && (
                        <>
                          <option value="">Select counselor...</option>
                          {counselors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </>
                      )}
                    </select>
                    {counselorsStatus === 'error' && (
                      <p className="text-[10px] text-destructive mt-1">
                        Couldn't load counselor list.{' '}
                        <button type="button" data-testid="walkin-counselor-retry" onClick={fetchCounselors} className="underline font-semibold">Retry</button>
                      </p>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Lead Source Channel *</Label>
                <select data-testid="walkin-channel" value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}
                  className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required>
                  <option value="">Select channel...</option>
                  {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            {showChannelDetail && (
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Referrer name / details</Label>
                <Input data-testid="walkin-channel-detail" value={form.channel_detail} onChange={e => setForm({ ...form, channel_detail: e.target.value })} className="h-9 rounded-lg text-sm" />
              </div>
            )}
          </div>

          {/* Contact details */}
          <div className="space-y-3 pt-3 border-t border-border">
            <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">Contact Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Email *</Label>
                <Input type="email" data-testid="walkin-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-9 rounded-lg text-sm" required />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Residence Phone</Label>
                <Input data-testid="walkin-phone-residence" value={form.phone_residence} onChange={e => setForm({ ...form, phone_residence: e.target.value })} className="h-9 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Address</Label>
              <Textarea data-testid="walkin-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="rounded-lg text-sm min-h-[50px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Date of Birth</Label>
                <Input type="date" data-testid="walkin-dob" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} className="h-9 rounded-lg text-sm" />
              </div>
            </div>
          </div>

          {/* Background */}
          <div className="space-y-3 pt-3 border-t border-border">
            <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">Background</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Qualification</Label>
                <Input data-testid="walkin-qualification" value={form.qualification} onChange={e => setForm({ ...form, qualification: e.target.value })} className="h-9 rounded-lg text-sm" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Occupation Status</Label>
                <select data-testid="walkin-occupation-status" value={form.occupation_status} onChange={e => setForm({ ...form, occupation_status: e.target.value })}
                  className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                  <option value="">Select...</option>
                  <option value="working">Working</option>
                  <option value="studying">Studying</option>
                  <option value="neither">Neither</option>
                </select>
              </div>
            </div>
            {form.occupation_status === 'working' && (
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Organization Name</Label>
                <Input data-testid="walkin-organization" value={form.organization_name} onChange={e => setForm({ ...form, organization_name: e.target.value })} className="h-9 rounded-lg text-sm" />
              </div>
            )}
            {form.occupation_status === 'studying' && (
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Institution Name</Label>
                <Input data-testid="walkin-institution" value={form.institution_name} onChange={e => setForm({ ...form, institution_name: e.target.value })} className="h-9 rounded-lg text-sm" />
              </div>
            )}
          </div>

          {/* Preferences */}
          <div className="space-y-3 pt-3 border-t border-border">
            <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">Preferences</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Secondary Interest</Label>
                <select data-testid="walkin-secondary-interest" value={form.secondary_interest} onChange={e => setForm({ ...form, secondary_interest: e.target.value })}
                  className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                  <option value="">None</option>
                  {Object.entries(courseGroups).map(([cat, list]) => (
                    <optgroup key={cat} label={cat}>
                      {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Batch Preference</Label>
                <select data-testid="walkin-batch-preference" value={form.batch_preference} onChange={e => setForm({ ...form, batch_preference: e.target.value })}
                  className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                  <option value="">Select...</option>
                  {BATCH_PREFERENCES.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-3 pt-3 border-t border-border">
            <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">Notes</h3>
            <div className="flex items-center gap-2">
              <Checkbox id="walkin-consent" data-testid="walkin-consent" checked={form.contact_consent} onCheckedChange={(v) => setForm({ ...form, contact_consent: !!v })} />
              <Label htmlFor="walkin-consent" className="text-xs font-normal text-foreground">May we contact on WhatsApp/SMS for follow-ups?</Label>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Office Notes</Label>
              <Textarea data-testid="walkin-office-notes" value={form.office_notes} onChange={e => setForm({ ...form, office_notes: e.target.value })} className="rounded-lg text-sm min-h-[60px]" />
            </div>
          </div>

          <Button type="submit" data-testid="walkin-submit-btn" disabled={submitting} className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
            {submitting ? 'Logging...' : 'Log Walk-in Enquiry'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
